import * as React from 'react';
import { DragEventHandler, FocusEventHandler, KeyboardEventHandler } from 'react';
import classNames from 'classnames';
import withStyles, { WithStyles } from 'react-jss';
import { compose } from 'redux';

import Tooltip from '../Tooltip';
import Icon, { IconsAvailable } from '../Icon';

import './styles.scss';
import { mergeDeep } from '../../Utilities/MergeDeep';

export interface IButtonProps {
    /** style to pass */
    style?: React.CSSProperties;
    /** size to use */
    size?: 'default' | 'small' | 'xsmall' | 'large';
    /** type of element to use */
    type?: 'button' | 'submit' | 'anchor';
    /** click event, should not be used with type 'anchor' */
    onClick?: (event) => void;
    /** hyperlink, should only be used with type 'anchor' */
    href?: string;
    /** style to use */
    layout?: 'default' | 'link' | 'icon' | 'none';
    /** intent variation */
    intent?: 'default' | 'primary' | 'success' | 'danger' | 'discrete' | 'secondary' | 'secondary-muted' | 'tertiary';
    /** title to use */
    title: React.ReactNode | string;
    /** disable clicking */
    disabled?: boolean;
    /** disable clicking and display loader */
    loading?: boolean;
    /** DOM target element name attribute */
    name?: string;
    /** DOM target element id attribute */
    id?: string;
    /** React-router link component to use instead of HTML A tag. If defined href is passed as "to" prop. */
    link?: any;
    /** Icon to display */
    icon?: IconsAvailable;
    /** Additional classes for icon */
    iconClassName?: string;
    /** className to append */
    className?: string;
    /** Tooltip text to display on hover */
    tooltip?: string;
    /** Configuration for aria-* attributes */
    ariaConfig?: IAriaConfig;
    onBlur?: FocusEventHandler;
    onDragEnter?: DragEventHandler;
    onDragLeave?: DragEventHandler;
    onDragOver?: DragEventHandler;
    onDrop?: DragEventHandler;
    onFocus?: FocusEventHandler;
    onKeyDown?: KeyboardEventHandler;
    /** Data test id for automated tests */
    testId?: string;
}

export interface IAriaConfig {
    label?: string;
    pressed?: boolean;
}

interface IState {
}

type Props = IButtonProps & WithStyles<typeof styles>;

/**
 * Displays button
 */
class Button extends React.Component<Props, IState> {
    static displayName: string = 'Button';

    private innerRef;

    /**
     * Focuses button element
     * @public
     */
    focus = () => {
        if (this.innerRef) {
            this.innerRef.focus();
        }
    };

    /**
     * Blurs button element
     * @public
     */
    blur = () => {
        if (this.innerRef) {
            this.innerRef.focus();
            this.innerRef.blur();
        }
    };

    render () {
        let Elem: any = 'button';
        if (this.props.type && this.props.type === 'anchor') {
            if (this.props.link) {
                Elem = this.props.link;
            } else {
                Elem = 'a';
            }
        }

        return (
            <Elem
                type={this.props.type && this.props.type !== 'anchor' ? this.props.type : undefined}
                className={classNames(this.props.classes.button, 'button', {
                    'disabled': this.props.disabled,
                    'loading': this.props.loading,

                    'layout-default': this.props.layout === 'default' || !this.props.layout,
                    'layout-link': this.props.layout === 'link',

                    'size-small': this.props.size === 'small',
                    'size-xsmall': this.props.size === 'xsmall',
                    'size-large': this.props.size === 'large',

                    'intent-default': this.props.intent === 'default' || !this.props.intent,
                    'intent-primary': this.props.intent === 'primary',
                    'intent-secondary': this.props.intent === 'secondary',
                    'intent-secondary muted': this.props.intent === 'secondary-muted',
                    'intent-tertiary': this.props.intent === 'tertiary',
                    'intent-success': this.props.intent === 'success',
                    'intent-danger': this.props.intent === 'danger',
                    'intent-discrete': this.props.intent === 'discrete',
                }, this.props.className)}
                style={this.props.style}
                onClick={(e) => {
                    if (this.props.onClick) {
                        this.props.onClick(e);
                    }
                }}
                {...(this.props.link ? { to: this.props.href } : { href: this.props.href })}
                name={this.props.name}
                id={this.props.id}
                ref={(node) => this.innerRef = node}
                onBlur={this.props.onBlur}
                onDragEnter={this.props.onDragEnter}
                onDragLeave={this.props.onDragLeave}
                onDragOver={this.props.onDragOver}
                onDrop={this.props.onDrop}
                onFocus={this.props.onFocus}
                onKeyDown={this.props.onKeyDown}
                aria-label={this.props.ariaConfig?.label}
                aria-pressed={this.props.ariaConfig?.pressed}
                data-testid={this.props.testId}
            >
                <span className={classNames('button-inner')}>
                        {this.props.icon && !this.props.loading && (
                            <Icon
                                className={classNames('icon', this.props.iconClassName)}
                                width={14}
                                height={14}
                                kind={this.props.icon}
                            />
                        )}
                    <span className="text">{this.props.title}</span>
                </span>
                {this.props.loading && (
                    <div className="loader">
                        <Icon width={14} height={14} kind="loading"/>
                    </div>
                )}
                {this.props.tooltip && (
                    <Tooltip
                        position="top"
                        interactionKind="hover"
                        usePortal={true}
                        content={this.props.tooltip}
                        children={<div/>}
                        className="button-tooltip"
                    />
                )}
            </Elem>
        );
    }
}

const styles = (theme) => {
    return mergeDeep(
        {
            button: {
                '&.layout-default': {
                    '&.intent-default': {
                        'color': theme.colors.gray.light,
                        'backgroundColor': theme.colors.gray.light,
                        '& .text, & .loader, & .icon': {
                            color: theme.colors.text.dark,
                        },
                    },
                    '&.intent-primary': {
                        color: theme.colors.primary,
                        backgroundColor: theme.colors.primary,
                    },
                },
                '&.layout-link': {
                    '&.intent-primary': {
                        color: theme.colors.primary,
                    },
                },
                '.text, .loader, .icon': {
                    color: theme.colors.white,
                },
            },
        },
        {
            button: {
                ...theme.colors.components.button,
            },
        }
    );
};

export default compose<React.ComponentClass<IButtonProps>>(
    withStyles(styles, { injectTheme: true })
)(Button);
